import type { ContactOrderSelectionState } from "../../../_logic/selection-state"
import type { ContactOrderPostalSelection } from "../../../../../../api/kehrblatt/contact-orders"
import type { RowCommunicationSelection } from "../../../_logic/planning-selection-contract"

type RowDraft = {
  digitalByCustomerId: RowCommunicationSelection["digitalByCustomerId"]
  postal: { enabled: boolean; choice: ContactOrderPostalSelection | null }
  revision: number
}

const same = (left: unknown, right: unknown) => JSON.stringify(left) === JSON.stringify(right)

/** One organization-bound draft owned by the overlay, not by a mounted step. */
export class ContactOrderPlanningDraft {
  readonly organizationId: string
  private rows = $state<Record<string, RowDraft>>({})
  valid = $state(true)
  removedEventIds = $state<string[]>([])
  private readonly operationIds = new Map<string, string>()

  constructor(organizationId: string, removedEventIds: string[] = []) {
    this.organizationId = organizationId
    this.removedEventIds = [...removedEventIds]
  }

  get selectionState(): ContactOrderSelectionState {
    return {
      communicationSelectionByEventId: Object.fromEntries(
        Object.entries(this.rows).map(([id, row]) => [
          id,
          {
            digitalByCustomerId: row.digitalByCustomerId,
            postal:
              row.postal.enabled && row.postal.choice ? row.postal.choice : { mode: "DISABLED" },
          },
        ]),
      ),
    }
  }

  set selectionState(next: ContactOrderSelectionState) {
    if (!this.valid) return

    const rows: Record<string, RowDraft> = {}
    for (const [id, selection] of Object.entries(next.communicationSelectionByEventId)) {
      const previous = this.rows[id]
      const candidate = {
        digitalByCustomerId: selection.digitalByCustomerId,
        postal: {
          enabled: selection.postal.mode !== "DISABLED",
          choice:
            selection.postal.mode !== "DISABLED"
              ? selection.postal
              : (previous?.postal.choice ?? null),
        },
      }
      const unchanged =
        previous &&
        same(candidate, {
          digitalByCustomerId: previous.digitalByCustomerId,
          postal: previous.postal,
        })
      rows[id] = unchanged ? previous : { ...candidate, revision: (previous?.revision ?? 0) + 1 }
    }

    if (!same(this.rows, rows)) this.rows = rows
  }

  configuredPostalFor(eventId: string): ContactOrderPostalSelection | null {
    return this.rows[eventId]?.postal.choice ?? null
  }

  revisionFor(eventId: string): number {
    return this.rows[eventId]?.revision ?? 0
  }

  configurePostal(
    eventId: string,
    choice: ContactOrderPostalSelection,
    enabled: boolean,
    current: RowCommunicationSelection,
  ): void {
    if (!this.valid) return

    const previous = this.rows[eventId]
    const candidate = {
      digitalByCustomerId: current.digitalByCustomerId,
      postal: {
        enabled: enabled && choice.mode !== "DISABLED",
        choice: choice.mode === "DISABLED" ? null : choice,
      },
    }

    if (
      previous &&
      same(candidate, {
        digitalByCustomerId: previous.digitalByCustomerId,
        postal: previous.postal,
      })
    )
      return

    this.rows[eventId] = { ...candidate, revision: (previous?.revision ?? 0) + 1 }
  }

  operationIdFor(workOrderId: string, eventId: string | null | undefined): string {
    if (!this.valid) throw new Error("Der Planungskontext ist nicht mehr gültig.")

    const key = JSON.stringify([workOrderId, eventId ?? null])
    let id = this.operationIds.get(key)

    if (!id) {
      id = crypto.randomUUID()
      this.operationIds.set(key, id)
    }

    return id
  }

  invalidate(): void {
    this.valid = false
    this.rows = {}
    this.removedEventIds = []
    this.operationIds.clear()
  }
}

export const createContactOrderPlanningDraft = (organizationId: string) =>
  new ContactOrderPlanningDraft(organizationId)
